export enum FetchMapType {
  ASSETS_QUERY = "ASSETS_QUERY",
  TICKETS_QUERY = "TICKETS_QUERY",
  NEARBY_FROM_CENTER = "NEARBY_FROM_CENTER",
  IDS = "IDS",
}

export type FetchAssetsData = {
  type: FetchMapType.ASSETS_QUERY
  data: Record<string, FilterQueryValue>
}

export type FetchNearbyData = {
  type: FetchMapType.NEARBY_FROM_CENTER
  data: {
    center: [number, number]
    distance: number
  }
}

export type FetchTicketsData = {
  type: FetchMapType.TICKETS_QUERY
  data: Record<string, FilterQueryValue>
}

export type FetchIdsData = {
  type: FetchMapType.IDS
  data: Array<string>
}

export type GeoAsset = DirectGeoAsset | NearbyGeoAsset
export type MapAsset = GeoAsset | LookupAsset

export interface DirectGeoAsset {
  _id: Asset["_id"]
  key: Asset["key"]
  primaryImage?: Asset["primaryImage"]
  description: string
  organization: Asset["organization"]
  category: Asset["category"]
  location: {
    city: AssetLocation["city"]
    gps: AssetLocation["gps"]
  }
  supplierTicketStatistics?: TicketUsageStatistic
}

export interface NearbyGeoAsset extends DirectGeoAsset {
  distance: number
}

export interface GeoCluster {
  name: string
  count: number
  center: [number, number]
  convexHull: Array<[number, number]>
}

export type FetchMapData = FetchAssetsData | FetchTicketsData | FetchIdsData | FetchNearbyData

export const useGeoAssetsStore = defineStore("geoAssets", () => {
  const DEFAULT_NEARBY_DISTANCE = 1000

  const getGeoAssets = (
    fetchMapData: FetchMapData,
    forcedQuery: Record<string, FilterQueryValue>,
    showNearby?: boolean,
  ): Promise<Array<GeoAsset>> => {
    const nearByDistance = showNearby ? DEFAULT_NEARBY_DISTANCE : undefined

    switch (fetchMapData.type) {
      case FetchMapType.ASSETS_QUERY:
        return getGeoAssetsByAssetQuery(fetchMapData.data, forcedQuery, nearByDistance)
      case FetchMapType.TICKETS_QUERY:
        return getGeoAssetsByTicketQuery(fetchMapData.data, forcedQuery, nearByDistance)
      case FetchMapType.NEARBY_FROM_CENTER:
        return getGeoAssetsNearby(forcedQuery, fetchMapData.data.center, fetchMapData.data.distance)
      case FetchMapType.IDS:
        return getGeoAssetsByIds(fetchMapData.data, forcedQuery, nearByDistance)
    }
  }

  const getGeoClusters = (
    fetchMapData: FetchMapData,
    forcedQuery: Record<string, FilterQueryValue>,
    showNearby?: boolean,
  ): Promise<Array<GeoCluster>> => {
    const nearByDistance = showNearby ? DEFAULT_NEARBY_DISTANCE : undefined

    switch (fetchMapData.type) {
      case FetchMapType.ASSETS_QUERY:
        return getGeoClustersByAssetQuery(fetchMapData.data, forcedQuery, nearByDistance)
      case FetchMapType.TICKETS_QUERY:
        return getGeoClustersByTicketQuery(fetchMapData.data, forcedQuery, nearByDistance)
      case FetchMapType.NEARBY_FROM_CENTER:
        return getGeoClustersNearby(fetchMapData.data.center, fetchMapData.data.distance)
      case FetchMapType.IDS:
        return getGeoClustersByIds(fetchMapData.data, forcedQuery, nearByDistance)
    }
  }

  const getGeoClustersByIds = async (
    ids: Array<string>,
    forcedQuery: Record<string, FilterQueryValue>,
    showNearbyFromDistance?: number,
  ): Promise<Array<GeoCluster>> => {
    const queryParameters = getUrlSearchParams({
      forcedQuery,
      showNearbyFromDistance,
    })

    const { data } = await useSamApi<Array<GeoCluster>>(`/geo-assets/clusters/get-by-asset-ids?${queryParameters}`, {
      method: "POST",
      body: JSON.stringify(ids),
    })

    return data
  }

  const getGeoClustersNearby = async (center: [number, number], distance: number): Promise<Array<GeoCluster>> => {
    const queryParameters = getUrlSearchParams({
      center,
      distance,
    })

    const { data } = await useSamApi<Array<GeoCluster>>(`/geo-assets/clusters/nearby?${queryParameters}`)
    return data
  }

  const getGeoClustersByAssetQuery = async (
    query: Record<string, FilterQueryValue>,
    forcedQuery: Record<string, FilterQueryValue>,
    showNearbyFromDistance?: number,
  ): Promise<Array<GeoCluster>> => {
    const queryParameters = getUrlSearchParams({
      query,
      forcedQuery,
      showNearbyFromDistance,
    })

    const { data } = await useSamApi<Array<GeoCluster>>(`/geo-assets/clusters?${queryParameters}`)
    return data
  }

  const getGeoClustersByTicketQuery = async (
    query: FilterQueryValue,
    forcedQuery: Record<string, FilterQueryValue>,
    showNearbyFromDistance?: number,
  ): Promise<Array<GeoCluster>> => {
    const queryParameters = getUrlSearchParams({
      query,
      forcedQuery,
      showNearbyFromDistance,
    })

    const { data } = await useSamApi<Array<GeoCluster>>(`/geo-assets/clusters/get-by-ticket-query?${queryParameters}`)
    return data
  }

  const getGeoAssetsByIds = async (
    ids: Array<string>,
    forcedQuery: Record<string, FilterQueryValue>,
    showNearbyFromDistance?: number,
  ): Promise<Array<GeoAsset>> => {
    const queryParameters = getUrlSearchParams({
      forcedQuery,
      showNearbyFromDistance,
    })

    const { data } = await useSamApi<Array<GeoAsset>>(`/geo-assets/get-by-asset-ids?${queryParameters}`, {
      method: "POST",
      body: JSON.stringify(ids),
    })

    return data
  }

  const getGeoAssetsByAssetQuery = async (
    query: Record<string, FilterQueryValue>,
    forcedQuery: Record<string, FilterQueryValue>,
    showNearbyFromDistance?: number,
  ): Promise<Array<GeoAsset>> => {
    const queryParameters = getUrlSearchParams({
      query,
      forcedQuery,
      showNearbyFromDistance,
    })

    const { data } = await useSamApi<Array<GeoAsset>>(`/geo-assets?${queryParameters}`)
    return data
  }

  const getGeoAssetsByTicketQuery = async (
    query: Record<string, FilterQueryValue>,
    forcedQuery: Record<string, FilterQueryValue>,
    showNearbyFromDistance?: number,
  ): Promise<Array<GeoAsset>> => {
    const queryParameters = getUrlSearchParams({
      query,
      forcedQuery,
      showNearbyFromDistance,
    })

    const { data } = await useSamApi<Array<GeoAsset>>(`/geo-assets/get-by-ticket-query?${queryParameters}`)
    return data
  }

  const getGeoAssetsNearby = async (
    forcedQuery: Record<string, FilterQueryValue>,
    center: [number, number],
    distance: number,
  ): Promise<Array<GeoAsset>> => {
    const queryParameters = getUrlSearchParams({
      forcedQuery,
      center: center.join(","),
      distance,
    })

    const { data } = await useSamApi<Array<GeoAsset>>(`/geo-assets/nearby?${queryParameters}`)
    return data
  }

  return {
    getGeoAssets,
    getGeoClusters,
  }
})
